import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ui/ErrorBoundary';
import LoadingSpinner from './components/ui/LoadingSpinner';
import PrivateRoute from './components/ui/PrivateRoute';
import { ToastProvider } from './context/ToastContext';
import { AuthProvider } from './context/AuthContext';
import { DashboardProvider } from './context/DashboardContext';
import Header from './components/ui/Header';
import Footer from './components/ui/Footer';

import Results from './pages/Results';
import MyMatches from './pages/MyMatches';
import AllMatches from './pages/AllMatches';
import PlayerMatches from './pages/PlayerMatches';
import ContactUs from './pages/ContactUs';
import VerifyEmail from './pages/VerifyEmail';
import CheckYourEmail from './pages/CheckYourEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Lazy load components for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Rankings = lazy(() => import('./pages/Rankings'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Map = lazy(() => import('./pages/Map'));
const PoolDetails = lazy(() => import('./pages/PoolDetails'));
const PlayerProfile = lazy(() => import('./pages/PlayerProfile'));
const ViewPools = lazy(() => import('./pages/ViewPools'));
const HowRankingsWork = lazy(() => import('./pages/HowRankingsWork'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));

const AppContent = () => {
    return (
        <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-grow">
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/check-your-email" element={<CheckYourEmail />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={
                            <PrivateRoute>
                                <Dashboard />
                            </PrivateRoute>
                        } />
                        <Route path="/rankings" element={
                            <PrivateRoute>
                                <Rankings />
                            </PrivateRoute>
                        } />
                        <Route path="/map" element={
                            <PrivateRoute>
                                <Map />
                            </PrivateRoute>
                        } />
                        <Route path="/pools/new" element={
                            <PrivateRoute>
                                <PoolDetails />
                            </PrivateRoute>
                        } />
                        <Route path="/pools/:poolName" element={
                            <PrivateRoute>
                                <PoolDetails />
                            </PrivateRoute>
                        } />
                        <Route path="/view-pools" element={
                            <PrivateRoute>
                                <ViewPools />
                            </PrivateRoute>
                        } />
                        <Route path="/player/:userId" element={
                            <PrivateRoute>
                                <PlayerProfile />
                            </PrivateRoute>
                        } />
                        <Route path="/results" element={
                            <PrivateRoute>
                                <Results />
                            </PrivateRoute>
                        } />
                        <Route path="/my-matches" element={<MyMatches />} />
                        <Route path="/player-matches/:userId" element={
                            <PrivateRoute>
                                <PlayerMatches />
                            </PrivateRoute>
                        } />
                        <Route path="/all-matches" element={<AllMatches />} />
                        <Route path="/how-rankings-work" element={
                            <PrivateRoute>
                                <HowRankingsWork />
                            </PrivateRoute>
                        } />
                        <Route path="/terms-and-conditions" element={
                            <PrivateRoute>
                                <TermsAndConditions />
                            </PrivateRoute>
                        } />
                        <Route path="/contact-us" element={
                            <PrivateRoute>
                                <ContactUs />
                            </PrivateRoute>
                        } />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </Suspense>
            </main>
            <Footer />
        </div>
    );
};

const App = () => {
    return (
        <ErrorBoundary>
            <Router>
                <ToastProvider>
                    <AuthProvider>
                        <DashboardProvider>
                            <AppContent />
                        </DashboardProvider>
                    </AuthProvider>
                </ToastProvider>
            </Router>
        </ErrorBoundary>
    );
};

export default App;
